/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component} from '@angular/core';

import {ApiConfig} from '../../config/api-config';
import template from './ai-how-connect-page.component.html';

export type AiAgentId = 'claude' | 'codex' | 'antigravity' | 'mistral';
export type ClaudeRegisterOption = 'account' | 'cli' | 'windows' | 'mac';
export type DesktopOs = 'windows' | 'mac' | 'linux';
export type ScriptOs = 'unix' | 'windows';

export const AI_AGENTS: {id: AiAgentId; name: string; summary: string; images: string[]}[] = [
  {id: 'claude', name: 'Anthropic Claude Code', summary: 'Claude Code app or command-line interface', images: ['/images/ai/claude.svg']},
  {id: 'codex', name: 'OpenAI Codex / ChatGPT', summary: 'Codex CLI, with an optional ChatGPT desktop app', images: ['/images/ai/codex.svg']},
  {id: 'antigravity', name: 'Google Antigravity', summary: 'Antigravity app for Windows, Mac, or Linux', images: ['/images/ai/antigravity.svg']},
  {id: 'mistral', name: 'Mistral Vibe', summary: 'Setup script for Mac, Linux, or Windows', images: ['/images/ai/mistral.svg']}
];

export const CLAUDE_REGISTER_OPTIONS: {id: ClaudeRegisterOption; name: string; summary: string; images: string[]}[] = [
  {id: 'account', name: 'Claude account', summary: 'Recommended. Available in all your Claude clients', images: ['/images/ai/claude.svg']},
  {id: 'cli', name: 'CLI', summary: 'Register Adnuntius in the Claude CLI only', images: ['/images/ai/terminal.svg']},
  {id: 'windows', name: 'Windows app', summary: 'Add a custom connector in the Windows app', images: ['/images/ai/windows.svg']},
  {id: 'mac', name: 'Mac app', summary: 'Use your Claude account instead', images: ['/images/ai/apple.svg']}
];

export const DESKTOP_OS_OPTIONS: {id: DesktopOs; name: string; images: string[]}[] = [
  {id: 'windows', name: 'Windows', images: ['/images/ai/windows.svg']},
  {id: 'mac', name: 'Mac', images: ['/images/ai/apple.svg']},
  {id: 'linux', name: 'Linux', images: ['/images/ai/linux.svg']}
];

export const SCRIPT_OS_OPTIONS: {id: ScriptOs; name: string; images: string[]}[] = [
  {id: 'unix', name: 'Mac or Linux', images: ['/images/ai/apple.svg', '/images/ai/linux.svg']},
  {id: 'windows', name: 'Windows', images: ['/images/ai/windows.svg']}
];

@Component({
  standalone: false,
  selector: 'adn-ai-how-connect-page',
  template,
  styles: [`
    .ai-choice {
      display: block;
      width: 100%;
      text-align: left;
      font: inherit;
      color: inherit;
      background: #fff;
      cursor: pointer;
    }
    .ai-choice:hover {
      border-color: #ccc;
    }
    .ai-choice.ai-choice-selected {
      border-color: #337ab7;
      box-shadow: 0 0 0 1px #337ab7;
    }
    .ai-choice-icons {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }
    .ai-choice-icon {
      display: block;
      width: 36px;
      height: 36px;
    }
    .ai-how-connect-command {
      margin: 12px 0 16px;
    }
    .ai-how-connect-command pre {
      margin-bottom: 8px;
      overflow-x: auto;
    }
  `]
})
export class AiHowConnectPageComponent {
  readonly mcpUrl = this.apiConfig.getUri('mcp');
  readonly agents = AI_AGENTS;
  readonly claudeRegisterOptions = CLAUDE_REGISTER_OPTIONS;

  constructor(private readonly apiConfig: ApiConfig) {
  }
  readonly desktopOsOptions = DESKTOP_OS_OPTIONS;
  readonly scriptOsOptions = SCRIPT_OS_OPTIONS;
  readonly claudeCliCommand = 'claude mcp add --transport http adnuntius ' + this.mcpUrl;
  readonly claudePersonalConnectorUrl = 'https://claude.ai/customize/connectors?modal=add-custom-connector';
  readonly claudeTeamConnectorUrl = 'https://claude.ai/admin-settings/connectors?modal=add-custom-connector';
  readonly claudeDownloadUrl = 'https://claude.com/download';
  readonly claudeCliInstallUrl = 'https://code.claude.com/docs/en/quickstart#step-1-install-claude-code';
  readonly claudeConnectorsUrl = 'https://claude.ai/customize/connectors';
  readonly codexCliInstallUrl = 'https://developers.openai.com/codex/quickstart?setup=cli';
  readonly codexAppInstallUrl = 'https://developers.openai.com/codex/quickstart?setup=app';
  readonly codexAddCommand = 'codex mcp add adnuntius --url ' + this.mcpUrl;
  readonly antigravityDownloadUrl = 'https://antigravity.google/download';
  readonly antigravityWindowsCommand = '$j=\'{"mcpServers":{"adnuntius":{"serverUrl":"' + this.mcpUrl + '"}}}\'; foreach($d in \'antigravity\',\'config\'){ $p="$env:USERPROFILE\\.gemini\\$d\\mcp_config.json"; New-Item -ItemType Directory -Force -Path (Split-Path $p) | Out-Null; [System.IO.File]::WriteAllText($p,$j) }';
  readonly antigravityUnixCommand = 'python3 -c "import os,json;d={\'mcpServers\':{\'adnuntius\':{\'serverUrl\':\'' + this.mcpUrl + '\'}}};[(os.makedirs(os.path.dirname(p),exist_ok=True) or json.dump(d,open(p,\'w\'),indent=2)) for p in [os.path.expanduser(\'~/.gemini/antigravity/mcp_config.json\'),os.path.expanduser(\'~/.gemini/config/mcp_config.json\')]]"';
  readonly mistralUnixCommand = 'curl -fsSL https://raw.githubusercontent.com/Adnuntius/documentation/master/mcp/mistral-vibe/install.sh | bash';
  readonly mistralWindowsCommand = 'iwr https://raw.githubusercontent.com/Adnuntius/documentation/master/mcp/mistral-vibe/install.ps1 -UseBasicParsing | iex';

  agent: AiAgentId | null = null;
  claudeOption: ClaudeRegisterOption = 'account';
  antigravityOs: DesktopOs = 'mac';
  mistralOs: ScriptOs = 'unix';
  showTroubleshooting = false;

  get selectedAgentName(): string {
    const selected = this.agents.find(item => item.id === this.agent);
    return selected ? selected.name : '';
  }

  selectAgent(agent: AiAgentId): void {
    this.agent = agent;
    this.showTroubleshooting = false;
  }

  startOver(): void {
    this.agent = null;
    this.claudeOption = 'account';
    this.antigravityOs = 'mac';
    this.mistralOs = 'unix';
    this.showTroubleshooting = false;
  }

  openTroubleshooting(): void {
    this.showTroubleshooting = true;
  }

  closeTroubleshooting(): void {
    this.showTroubleshooting = false;
  }
}
